/**
 * Presentation settings panel.
 * Owns all presenter/admin/viewer UI and keeps Firestore/data wiring in one place.
 */

import type { AuthService, ChatRoleService, LecturaUser } from '../core/types';
import type {
  PresentationDirectoryEntry,
  PresentationDocState,
  PresentationManagementService,
  PresentationRequestState
} from '../services/presentation-firestore';

export interface BuildPresentationPanelOptions {
  authService: AuthService;
  roleService?: ChatRoleService;
  presentationService: PresentationManagementService;
  getPresentationId?: () => string;
}

interface PresentationPanelState {
  isHydrating: boolean;
  presentation: PresentationDocState | null;
  requests: PresentationRequestState[];
  myRequest: PresentationRequestState | null;
  searchToken: number;
  busyPresenterEmail: string;
  busyRequestId: string;
  busyPrivacy: boolean;
  banner: string;
  privacyBanner: string;
  requestBanner: string;
  viewerBanner: string;
  /** Normalized emails of active global admins (from Firestore). */
  adminEmails: string[];
  /** userDirectory profile snippets keyed by normalized email (photos for presenter rows). */
  userProfileByEmail: Record<string, { photoURL?: string; displayName?: string }>;
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function presPanelDebug(...args: unknown[]): void {
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug('[lectura-next][presentation-settings]', ...args);
  }
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/"/g, '&quot;');
}

function normalizeText(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function getActivePresentationId(): string {
  if (typeof window === 'undefined') return '';
  return String(window.__LECTURA_PRES_ID__ || '').trim();
}

function avatarLabel(entry: PresentationDirectoryEntry | PresentationRequestState, fallback = '?'): string {
  const name = String((entry as PresentationDirectoryEntry).displayName || (entry as PresentationRequestState).displayName || '').trim();
  const email = String((entry as PresentationDirectoryEntry).email || (entry as PresentationRequestState).email || '').trim();
  return (name || email || fallback).charAt(0).toUpperCase();
}

function displayName(entry: PresentationDirectoryEntry | PresentationRequestState): string {
  return String((entry as PresentationDirectoryEntry).displayName || (entry as PresentationRequestState).displayName || '').trim();
}

function emailAddress(entry: PresentationDirectoryEntry | PresentationRequestState): string {
  return String((entry as PresentationDirectoryEntry).email || (entry as PresentationRequestState).email || '').trim();
}

function isSafePhotoUrl(url: string): boolean {
  const u = String(url || '').trim();
  return /^https:\/\//i.test(u);
}

/** Circular avatar: photo or initial letter. */
function avatarPhotoOrLetterHtml(photoURL: string | undefined, letter: string, size: 'md' | 'sm'): string {
  const sm = size === 'sm' ? ' rf-pres-avatar-sm' : '';
  if (photoURL && isSafePhotoUrl(photoURL)) {
    return (
      '<div class="rf-pres-avatar rf-pres-avatar-photo' +
      sm +
      '" aria-hidden="true">' +
      '<img src="' +
      escapeAttr(photoURL) +
      '" alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer" />' +
      '</div>'
    );
  }
  return '<div class="rf-pres-avatar' + sm + '">' + escapeHtml(letter) + '</div>';
}

function hasPresenterControls(roleService?: ChatRoleService): boolean {
  const role = roleService?.getRole?.() ?? 'viewer';
  return role === 'presenter' || role === 'admin';
}

function isManagerRole(role: string): boolean {
  return role === 'presenter' || role === 'admin';
}

export function buildPresentationPanelContent(container: HTMLElement, opts: BuildPresentationPanelOptions): void {
  const { authService, roleService, presentationService, getPresentationId } = opts;
  const state: PresentationPanelState = {
    isHydrating: true,
    presentation: null,
    requests: [],
    myRequest: null,
    searchToken: 0,
    busyPresenterEmail: '',
    busyRequestId: '',
    busyPrivacy: false,
    banner: '',
    privacyBanner: '',
    requestBanner: '',
    viewerBanner: '',
    adminEmails: [],
    userProfileByEmail: {}
  };
  let disposed = false;
  let searchTimer = 0;
  /** Last typed filter text; restored after full manager re-renders so the field stays stable. */
  let presenterSearchDraft = '';
  /** Tracks active realtime stream scope: presentation + user + manager/viewer mode. */
  let liveScopeKey = '';
  let stopLivePresentation: (() => void) | null = null;
  let stopLiveRequests: (() => void) | null = null;
  let stopLiveMyRequest: (() => void) | null = null;
  let stopLiveAdmins: (() => void) | null = null;
  let lastKnownRole = String(roleService?.getRole?.() || 'viewer');
  let demotionReloadScheduled = false;

  const currentUser = (): LecturaUser | null => authService.getCurrentUser?.() ?? null;
  const authReady = (): boolean => authService.isAuthReady?.() ?? true;
  const presId = (): string => String(getPresentationId?.() || getActivePresentationId() || '').trim();
  const canManage = (): boolean => hasPresenterControls(roleService);

  function currentPresenterEmails(): string[] {
    return state.presentation?.presenters || [];
  }

  function existingPresenterSet(): Set<string> {
    return new Set(currentPresenterEmails().map((email) => normalizeText(email)));
  }

  function adminEmailSet(): Set<string> {
    return new Set(state.adminEmails.map((e) => normalizeText(e)));
  }

  function isEmailLikeStr(value: string): boolean {
    return /.+@.+\..+/.test(normalizeText(value));
  }

  /** Immediate avatar after add (suggestion / approve); directory lookup can refine on next full refresh. */
  function mergeUserProfilePhoto(normEmail: string, photoURL: string | undefined): void {
    if (!photoURL || !isSafePhotoUrl(photoURL)) return;
    state.userProfileByEmail = {
      ...state.userProfileByEmail,
      [normEmail]: { ...state.userProfileByEmail[normEmail], photoURL }
    };
  }

  function renderLoading(): string {
    return (
      '<section class="rf-pres-page">' +
      '<div class="rf-pres-card rf-pres-card-compact rf-pres-skeleton">' +
      '<p class="rf-pres-kicker">Presentation</p>' +
      '<div class="rf-pres-skeleton-line rf-pres-skeleton-line-wide"></div>' +
      '<div class="rf-pres-skeleton-line rf-pres-skeleton-line-medium"></div>' +
      '<p class="rf-pres-status">Loading account status…</p>' +
      '</div></section>'
    );
  }

  function renderSignin(): string {
    return (
      '<section class="rf-pres-page">' +
      '<div class="rf-pres-card rf-pres-hero">' +
      '<p class="rf-pres-kicker">Presentation access</p>' +
      '<h2 class="rf-pres-title">Sign in to manage presenters</h2>' +
      '<p class="rf-pres-lead">You need a signed-in account to request presenter access or manage this presentation.</p>' +
      '<div class="rf-pres-action-row">' +
      '<button type="button" class="rf-pres-button rf-pres-button-primary" data-action="signin-google">Sign in with Google</button>' +
      '<button type="button" class="rf-pres-button rf-pres-button-secondary" data-action="signin-github">Sign in with GitHub</button>' +
      '</div>' +
      '</div></section>'
    );
  }

  /** True when presentation doc lists this user as a presenter (normalized email). */
  function isCurrentUserListedPresenter(): boolean {
    const email = normalizeText(currentUser()?.email || '');
    if (!email || !isEmailLikeStr(email)) return false;
    return existingPresenterSet().has(email);
  }

  function viewerRequestStatusMessage(): string {
    const st = state.myRequest?.status;
    if (st === 'pending') return 'Your presenter request is pending review.';
    if (st === 'denied') return 'Your previous presenter request was denied. You can request again.';
    if (st === 'approved') {
      const presLoaded = state.presentation !== null;
      const listed = isCurrentUserListedPresenter();
      if (presLoaded && !listed) {
        return 'Presenter access for this deck was removed. You can request access again below if you still need it.';
      }
      if (presLoaded && listed) {
        return 'You are listed as a presenter. If controls still show as viewer, reload the page or reopen settings.';
      }
      return 'Your request was approved. Reload if presenter options do not appear—or request again if your access was removed.';
    }
    return 'You have not requested presenter rights yet.';
  }

  /** Roster rows: admins first, then non-admin presenters (same order as manager view). */
  function buildPresenterRosterRows(): Array<{ kind: 'admin' | 'presenter'; email: string }> {
    const admins = state.adminEmails
      .map((e) => String(e).trim())
      .filter((e) => isEmailLikeStr(e))
      .sort((a, b) => a.localeCompare(b));
    const adminNorm = new Set(admins.map((e) => normalizeText(e)));
    const rows: Array<{ kind: 'admin' | 'presenter'; email: string }> = [];
    for (const email of admins) rows.push({ kind: 'admin', email });
    for (const email of currentPresenterEmails()) {
      if (!adminNorm.has(normalizeText(email))) rows.push({ kind: 'presenter', email });
    }
    return rows;
  }

  function renderViewerPresenterRoster(): string {
    const rows = buildPresenterRosterRows();
    const head =
      '<div class="rf-pres-card">' +
      '<p class="rf-pres-kicker">Presentation</p>' +
      '<h3 class="rf-pres-section-title">Presenters</h3>';

    if (!rows.length) {
      return (
        head +
        '<p class="rf-pres-lead rf-pres-roster-empty">No presenters are listed for this deck yet.</p>' +
        '</div>'
      );
    }

    const items = rows
      .map((row) => {
        const norm = normalizeText(row.email);
        const prof = state.userProfileByEmail[norm];
        const dn = String(prof?.displayName || '').trim();
        const name =
          dn ||
          (row.kind === 'admin' ? 'Administrator' : 'Presenter');
        const letter = (dn || name).charAt(0).toUpperCase() || '?';
        const photo = prof?.photoURL && isSafePhotoUrl(prof.photoURL) ? prof.photoURL : undefined;
        return (
          '<div class="rf-pres-roster-item">' +
          avatarPhotoOrLetterHtml(photo, letter, 'md') +
          '<div class="rf-pres-roster-main">' +
          '<span class="rf-pres-roster-name">' +
          escapeHtml(name) +
          '</span>' +
          (row.kind === 'admin'
            ? '<span class="rf-pres-chip rf-pres-chip-accent rf-pres-roster-badge">Admin</span>'
            : '') +
          '</div></div>'
        );
      })
      .join('');

    return (
      head +
      '<p class="rf-pres-lead rf-pres-roster-lead">People who can present or help manage this deck.</p>' +
      '<div class="rf-pres-roster" aria-label="Presenters">' +
      items +
      '</div></div>'
    );
  }

  function renderViewer(): string {
    const requestStatus = viewerRequestStatusMessage();
    const disabled = state.myRequest?.status === 'pending' || state.viewerBanner === 'Submitting presenter request…';
    return (
      '<section class="rf-pres-page">' +
      '<div class="rf-pres-card">' +
      '<div class="rf-pres-row">' +
      '<div>' +
      '<p class="rf-pres-kicker">Presentation</p>' +
      '<h2 class="rf-pres-title">Request presenter rights</h2>' +
      '</div>' +
      '<span class="rf-pres-chip rf-pres-chip-muted">Viewer</span>' +
      '</div>' +
      '<p class="rf-pres-lead">You are currently a viewer for this presentation. If you need presenter access, you can request it here.</p>' +
      '<button type="button" class="rf-pres-button rf-pres-button-primary rf-pres-button-block mt-2 mb-2" data-action="request-presenter"' +
      (disabled ? ' disabled' : '') +
      '>Request presenter rights</button>' +
      '<p class="rf-pres-status ">' +
      escapeHtml(state.viewerBanner || requestStatus) +
      '</p>' +
      '</div>' +
      renderViewerPresenterRoster() +
      '</section>'
    );
  }

  function renderPresenters(): string {
    const admins = state.adminEmails
      .map((e) => String(e).trim())
      .filter((e) => isEmailLikeStr(e))
      .sort((a, b) => a.localeCompare(b));
    const adminNorm = new Set(admins.map((e) => normalizeText(e)));
    const presenters = currentPresenterEmails();
    const myNorm = normalizeText(currentUser()?.email || '');

    type Row = { kind: 'admin' | 'presenter'; email: string };
    const rows: Row[] = [];
    for (const email of admins) {
      rows.push({ kind: 'admin', email });
    }
    for (const email of presenters) {
      if (!adminNorm.has(normalizeText(email))) {
        rows.push({ kind: 'presenter', email });
      }
    }

    if (!rows.length) {
      return '<div class="rf-pres-empty-wrap"><p class="rf-pres-empty">No presenters assigned yet.</p></div>';
    }

    const hasAdminBackstop = admins.length > 0;
    function canRemovePresenter(email: string, isSelf: boolean): boolean {
      if (!presenters.some((p) => normalizeText(p) === normalizeText(email))) return false;
      return hasAdminBackstop || !isSelf || presenters.length > 1;
    }

    return (
      '<div class="rf-pres-list">' +
      rows
        .map((row) => {
          const normalized = normalizeText(row.email);
          const isSelf = myNorm === normalized;
          if (row.kind === 'admin') {
            const prof = state.userProfileByEmail[normalized];
            return (
              '<div class="rf-pres-item">' +
              avatarPhotoOrLetterHtml(prof?.photoURL, (row.email || '?').charAt(0).toUpperCase(), 'md') +
              '<div class="rf-pres-item-main">' +
              '<div class="rf-pres-item-title">' +
              escapeHtml(row.email) +
              '<span class="rf-pres-chip rf-pres-chip-accent">Admin</span>' +
              (isSelf ? '<span class="rf-pres-chip rf-pres-chip-muted">You</span>' : '') +
              '</div>' +
              '</div>' +
              '<div class="rf-pres-item-actions"></div>' +
              '</div>'
            );
          }
          const removable = canRemovePresenter(row.email, isSelf);
          const profP = state.userProfileByEmail[normalized];
          return (
            '<div class="rf-pres-item">' +
            avatarPhotoOrLetterHtml(profP?.photoURL, (row.email || '?').charAt(0).toUpperCase(), 'md') +
            '<div class="rf-pres-item-main">' +
            '<div class="rf-pres-item-title">' +
            escapeHtml(row.email) +
            (isSelf ? '<span class="rf-pres-chip rf-pres-chip-accent">You</span>' : '') +
            '</div>' +
            '</div>' +
            '<div class="rf-pres-item-actions">' +
            (removable
              ? '<button type="button" class="rf-pres-button rf-pres-button-danger" data-action="remove-presenter" data-email="' +
                escapeAttr(row.email) +
                '"' +
                (state.busyPresenterEmail === normalized ? ' disabled' : '') +
                '>Remove</button>'
              : '') +
            '</div>' +
            '</div>'
          );
        })
        .join('') +
      '</div>'
    );
  }

  function buildSuggestionListHtml(items: PresentationDirectoryEntry[]): string {
    if (!items.length) return '';
    return (
      '<div class="rf-pres-suggestions">' +
      items
        .map((entry) => {
          const label = displayName(entry) || emailAddress(entry);
          const em = emailAddress(entry);
          const avatarInner = avatarPhotoOrLetterHtml(entry.photoURL, avatarLabel(entry), 'sm')
            .replace(/<div /g, '<span ')
            .replace(/<\/div>/g, '</span>');
          const photoAttr =
            entry.photoURL && isSafePhotoUrl(String(entry.photoURL).trim())
              ? ' data-photo-url="' + escapeAttr(String(entry.photoURL).trim()) + '"'
              : '';
          return (
            '<button type="button" class="rf-pres-suggestion" data-action="add-suggestion" data-email="' +
            escapeAttr(em) +
            '"' +
            photoAttr +
            ' aria-label="' +
            escapeAttr('Add presenter ' + em) +
            '">' +
            avatarInner +
            '<span class="rf-pres-suggestion-text">' +
            '<span class="rf-pres-suggestion-name">' +
            escapeHtml(label) +
            '</span>' +
            '<span class="rf-pres-suggestion-email">' +
            escapeHtml(emailAddress(entry)) +
            '</span>' +
            '</span>' +
            '<span class="rf-pres-chip rf-pres-chip-muted">Add</span>' +
            '</button>'
          );
        })
        .join('') +
      '</div>'
    );
  }

  /** Like legacy lectura: only the suggestion list updates — input is never torn down on filter. */
  function paintSuggestionList(items: PresentationDirectoryEntry[]): void {
    const host = container.querySelector('.rf-pres-suggestions-host');
    if (!host) return;
    host.innerHTML = buildSuggestionListHtml(items);
  }

  function renderRequests(): string {
    if (!state.requests.length) {
      return '<div class="rf-pres-empty-wrap"><p class="rf-pres-empty">No pending presenter requests.</p></div>';
    }
    return (
      '<div class="rf-pres-list">' +
      state.requests
        .map((request) => {
          const requestEmail = emailAddress(request);
          return (
            '<div class="rf-pres-item">' +
            avatarPhotoOrLetterHtml(request.photoURL, avatarLabel(request), 'md') +
            '<div class="rf-pres-item-main">' +
            '<div class="rf-pres-item-title">' +
            escapeHtml(displayName(request) || requestEmail) +
            '</div>' +
            '<p class="rf-pres-item-meta">' +
            escapeHtml(requestEmail) +
            '</p>' +
            '</div>' +
            '<div class="rf-pres-item-actions rf-pres-item-actions-stack">' +
            '<button type="button" class="rf-pres-button rf-pres-button-primary" data-action="approve-request" data-request-id="' +
            escapeAttr(request.id) +
            '"' +
            (state.busyRequestId === request.id ? ' disabled' : '') +
            '>Allow</button>' +
            '<button type="button" class="rf-pres-button rf-pres-button-danger" data-action="deny-request" data-request-id="' +
            escapeAttr(request.id) +
            '"' +
            (state.busyRequestId === request.id ? ' disabled' : '') +
            '>Deny</button>' +
            '</div>' +
            '</div>'
          );
        })
        .join('') +
      '</div>'
    );
  }

  function renderManager(): string {
    const mgrIds = new Set([
      ...state.adminEmails.map((e) => normalizeText(e)),
      ...currentPresenterEmails().map((e) => normalizeText(e))
    ]);
    const nPresenters = mgrIds.size;
    const nRequests = state.requests.length;

    const presenterHelp = state.banner ? '<p class="rf-pres-status">' + escapeHtml(state.banner) + '</p>' : '';
    const requestHelp = state.requestBanner ? '<p class="rf-pres-status">' + escapeHtml(state.requestBanner) + '</p>' : '';

    const isPrivate = !!state.presentation?.isPrivate;
    const publicLabelClass = isPrivate ? 'rf-pres-mode-label rf-pres-mode-label-muted' : 'rf-pres-mode-label rf-pres-mode-label-active';
    const privateLabelClass = isPrivate ? 'rf-pres-mode-label rf-pres-mode-label-active' : 'rf-pres-mode-label rf-pres-mode-label-muted';

    return (
      '<section class="rf-pres-page">' +
      '<div class="rf-pres-grid">' +
      '<div class="rf-pres-card rf-pres-card-privacy-hidden">' +
      '<p class="rf-pres-kicker">Presentation</p>' +
      '<h2 class="rf-pres-title">Manage privacy settings</h2>' +
      '<p class="rf-pres-lead">Private presentations are still accessible by direct link for authorized presenters and admins, but are hidden from public discovery lists.</p>' +
      '<div class="rf-pres-privacy-toggle-row">' +
      '<span class="' +
      publicLabelClass +
      '">Public</span>' +
      '<label class="rf-pres-switch" aria-label="Toggle private listing (hidden from discovery when private)">' +
      '<input type="checkbox" class="rf-pres-switch-input" data-action="toggle-privacy"' +
      (isPrivate ? ' checked' : '') +
      (state.busyPrivacy ? ' disabled' : '') +
      ' />' +
      '<span class="rf-pres-switch-track"></span>' +
      '</label>' +
      '<span class="' +
      privateLabelClass +
      '">Private</span>' +
      '</div>' +
      (state.privacyBanner
        ? '<p class="rf-pres-privacy-status" aria-live="polite">' + escapeHtml(state.privacyBanner) + '</p>'
        : '') +
      '</div>' +
      '<div class="rf-pres-card">' +
      '<div class="rf-pres-row rf-pres-row-start">' +
      '<div>' +
      '<p class="rf-pres-kicker">Presentation</p>' +
      '<h3 class="rf-pres-section-title">Manage presenters</h3>' +
      '</div>' +
      '<span class="rf-pres-chip rf-pres-chip-muted" aria-label="' +
      escapeAttr(`${nPresenters} presenters`) +
      '">' +
      escapeHtml(String(nPresenters)) +
      '</span>' +
      '</div>' +
      '<div class="rf-pres-field">' +
      '<label class="rf-pres-field-label" for="rf-pres-presenter-query">Add presenter</label>' +
      '<div class="rf-pres-field-row">' +
      '<input id="rf-pres-presenter-query" type="email" class="rf-pres-input" data-field="presenter-search" placeholder="Type a name or email address" autocomplete="off"' +
      (state.busyPresenterEmail ? ' disabled' : '') +
      ' />' +
      '<button type="button" class="rf-pres-button rf-pres-button-primary" data-action="add-presenter"' +
      (state.busyPresenterEmail ? ' disabled' : '') +
      '>Add</button>' +
      '</div>' +
      '<div class="rf-pres-suggestions-host"></div>' +
      '</div>' +
      presenterHelp +
      renderPresenters() +
      '</div>' +
      '<div class="rf-pres-card">' +
      '<div class="rf-pres-row rf-pres-row-start">' +
      '<div>' +
      '<p class="rf-pres-kicker">Presentation</p>' +
      '<h3 class="rf-pres-section-title">Review presenter requests</h3>' +
      '</div>' +
      '<span class="rf-pres-chip rf-pres-chip-muted" aria-label="' +
      escapeAttr(`${nRequests} pending requests`) +
      '">' +
      escapeHtml(String(nRequests)) +
      '</span>' +
      '</div>' +
      '<p class="rf-pres-lead">Review access requests from signed-in users who want presenter rights for this deck.</p>' +
      renderRequests() +
      requestHelp +
      '</div>' +
      '</div></section>'
    );
  }

  function render(): void {
    if (disposed) return;
    if (!presentationService.available) {
      container.innerHTML =
        '<section class="rf-pres-page"><div class="rf-pres-card rf-pres-card-compact"><p class="rf-pres-empty">Presentation management is not available in this environment.</p></div></section>';
      return;
    }
    if (!authReady() || state.isHydrating) {
      container.innerHTML = renderLoading();
      return;
    }
    const user = currentUser();
    if (!user || user.isAnonymous) {
      container.innerHTML = renderSignin();
      return;
    }
    if (canManage()) {
      container.innerHTML = renderManager();
      const nextInput = container.querySelector('[data-field="presenter-search"]') as HTMLInputElement | null;
      if (nextInput) {
        nextInput.value = presenterSearchDraft;
        if (presenterSearchDraft.trim().length >= 1) {
          scheduleSearch();
        }
      }
      return;
    }
    container.innerHTML = renderViewer();
  }

  function scheduleSearch(): void {
    if (searchTimer) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      void runSearch();
    }, 180);
  }

  async function runSearch(): Promise<void> {
    const inp = container.querySelector('[data-field="presenter-search"]') as HTMLInputElement | null;
    if (!inp || !container.contains(inp)) return;
    const query = inp.value.trim();
    const token = ++state.searchToken;
    if (query.length < 1) {
      paintSuggestionList([]);
      return;
    }
    try {
      const items = await presentationService.searchUserDirectory(query, 6);
      if (disposed || token !== state.searchToken) return;
      const used = existingPresenterSet();
      const currentEmail = normalizeText(currentUser()?.email || '');
      const admins = adminEmailSet();
      const filtered = (items || []).filter((entry) => {
        const email = normalizeText(entry.email);
        return !!email && email !== currentEmail && !used.has(email) && !admins.has(email);
      });
      paintSuggestionList(filtered);
    } catch {
      if (disposed || token !== state.searchToken) return;
      paintSuggestionList([]);
    }
  }

  function stopLiveStreams(): void {
    if (stopLivePresentation) {
      stopLivePresentation();
      stopLivePresentation = null;
    }
    if (stopLiveRequests) {
      stopLiveRequests();
      stopLiveRequests = null;
    }
    if (stopLiveMyRequest) {
      stopLiveMyRequest();
      stopLiveMyRequest = null;
    }
    if (stopLiveAdmins) {
      stopLiveAdmins();
      stopLiveAdmins = null;
    }
    liveScopeKey = '';
  }

  function refreshRosterProfiles(scopeKey: string): void {
    const profileEmails = [
      ...state.adminEmails,
      ...currentPresenterEmails().map((e) => normalizeText(e))
    ].filter((e, i, a) => isEmailLikeStr(e) && a.indexOf(e) === i);
    void presentationService
      .lookupUserProfilesByEmails(profileEmails)
      .then((map) => {
        if (disposed || liveScopeKey !== scopeKey) return;
        state.userProfileByEmail = map;
        render();
      })
      .catch(() => {});
  }

  function ensureLiveStreams(id: string, manage: boolean): void {
    const user = currentUser();
    const uid = String(user?.uid || '').trim();
    const scopeKey = `${id}|${uid}|${manage ? 'manage' : 'viewer'}`;
    if (scopeKey === liveScopeKey) return;

    stopLiveStreams();
    liveScopeKey = scopeKey;

    stopLivePresentation = presentationService.subscribePresentationDoc(
      id,
      (doc) => {
        if (disposed || liveScopeKey !== scopeKey) return;
        state.presentation = doc;
        refreshRosterProfiles(scopeKey);
        render();
      },
      (err) => presPanelDebug('subscribePresentationDoc failed', err)
    );

    stopLiveAdmins = presentationService.subscribeActiveAdminEmails(
      (emails) => {
        if (disposed || liveScopeKey !== scopeKey) return;
        state.adminEmails = emails.map((e) => normalizeText(e)).filter((e) => isEmailLikeStr(e));
        refreshRosterProfiles(scopeKey);
        render();
      },
      (err) => presPanelDebug('subscribeActiveAdminEmails failed', err)
    );

    stopLiveMyRequest = presentationService.subscribeMyPresenterRequest(
      id,
      (myRequest) => {
        if (disposed || liveScopeKey !== scopeKey) return;
        const prevStatus = state.myRequest?.status || '';
        const nextStatus = myRequest?.status || '';
        state.myRequest = myRequest;
        if (prevStatus !== nextStatus && (nextStatus === 'approved' || nextStatus === 'denied')) {
          // Request review can change access on private decks where doc listeners may be gated.
          void refreshPresentationData({ refreshRole: true });
          return;
        }
        render();
      },
      (err) => presPanelDebug('subscribeMyPresenterRequest failed', err)
    );

    if (manage) {
      stopLiveRequests = presentationService.subscribePresenterRequests(
        id,
        (requests) => {
          if (disposed || liveScopeKey !== scopeKey) return;
          state.requests = requests;
          render();
        },
        (err) => presPanelDebug('subscribePresenterRequests failed', err)
      );
    } else {
      state.requests = [];
      stopLiveRequests = null;
    }
  }

  async function refreshPresentationData(options?: { refreshRole?: boolean }): Promise<void> {
    if (disposed) return;
    const user = currentUser();
    if (!authReady()) {
      stopLiveStreams();
      state.isHydrating = true;
      render();
      return;
    }
    if (!user || user.isAnonymous) {
      stopLiveStreams();
      state.isHydrating = false;
      render();
      return;
    }
    const id = presId();
    if (!id) {
      stopLiveStreams();
      state.isHydrating = false;
      container.innerHTML = '<section class="rf-pres-page"><div class="rf-pres-card rf-pres-card-compact"><p class="rf-pres-empty">Open a presentation to manage presenters.</p></div></section>';
      return;
    }
    state.isHydrating = true;
    render();
    try {
      if (options?.refreshRole) {
        await Promise.resolve(roleService?.refresh?.()).catch(() => {});
      }
      const manage = canManage();
      presPanelDebug('refreshPresentationData start', {
        presId: id,
        canManage: manage,
        role: roleService?.getRole?.() ?? 'unknown'
      });
      const [presentation, requests, myRequest, adminEmails] = await Promise.all([
        presentationService.getPresentationDoc(id).catch((err) => {
          presPanelDebug('getPresentationDoc failed', err);
          return null;
        }),
        manage
          ? presentationService.getPresenterRequests(id).catch((err) => {
              presPanelDebug('getPresenterRequests failed', err);
              return [];
            })
          : Promise.resolve([]),
        presentationService.getMyPresenterRequest(id).catch((err) => {
          presPanelDebug('getMyPresenterRequest failed', err);
          return null;
        }),
        presentationService.listActiveAdminEmails().catch((err) => {
          presPanelDebug('listActiveAdminEmails promise rejection (unexpected if service catches)', err);
          return [];
        })
      ]);
      state.presentation = presentation;
      state.requests = requests as PresentationRequestState[];
      state.myRequest = myRequest as PresentationRequestState | null;
      state.adminEmails = (adminEmails as string[]).map((e) => normalizeText(e)).filter((e) => isEmailLikeStr(e));
      const profileEmails = [
        ...state.adminEmails,
        ...((presentation as PresentationDocState | null)?.presenters || []).map((e) => normalizeText(e))
      ].filter((e, i, a) => isEmailLikeStr(e) && a.indexOf(e) === i);
      state.userProfileByEmail = await presentationService
        .lookupUserProfilesByEmails(profileEmails)
        .catch(() => ({}));
      presPanelDebug('refreshPresentationData done', {
        presId: id,
        hasPresentation: !!presentation,
        presentersOnDoc: presentation?.presenters?.length ?? 0,
        requestCount: (requests as PresentationRequestState[]).length,
        adminEmailsLoaded: state.adminEmails.length,
        adminEmails: state.adminEmails
      });
      ensureLiveStreams(id, manage);
    } finally {
      state.isHydrating = false;
      render();
    }
  }

  function handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const button = target?.closest<HTMLElement>('[data-action]');
    if (!button || !container.contains(button)) return;
    const action = String(button.getAttribute('data-action') || '');
    if (action === 'signin-google') {
      Promise.resolve(authService.signInGoogle?.()).catch(() => {});
      return;
    }
    if (action === 'signin-github') {
      Promise.resolve(authService.signInGithub?.()).catch(() => {});
      return;
    }
    if (action === 'request-presenter') {
      if (!presId()) return;
      state.viewerBanner = 'Submitting presenter request…';
      render();
      presentationService
        .submitPresenterRequest(presId())
        .then(() => presentationService.getMyPresenterRequest(presId()))
        .then((myRequest) => {
          state.myRequest = myRequest;
          state.viewerBanner = 'Presenter request submitted.';
        })
        .catch(() => {
          state.viewerBanner = 'Could not submit presenter request.';
        })
        .finally(() => {
          state.isHydrating = false;
          render();
        });
      return;
    }
    if (action === 'toggle-privacy') {
      const input = button as HTMLInputElement;
      const next = !!input.checked;
      if (!presId()) return;
      state.busyPrivacy = true;
      state.privacyBanner = next ? 'Saving privacy…' : 'Saving visibility…';
      render();
      presentationService
        .updatePresentationPrefs(presId(), { isPrivate: next })
        .then(() => presentationService.getPresentationDoc(presId()))
        .then((doc) => {
          state.presentation = doc;
          state.privacyBanner = '';
        })
        .catch(() => {
          state.privacyBanner = 'Could not save presentation visibility.';
        })
        .finally(() => {
          state.busyPrivacy = false;
          render();
        });
      return;
    }
    if (action === 'add-presenter') {
      const inp = container.querySelector('[data-field="presenter-search"]') as HTMLInputElement | null;
      tryAddPresenter(String(inp?.value || ''));
      return;
    }
    if (action === 'add-suggestion') {
      const em = String(button.getAttribute('data-email') || '');
      const photo = String(button.getAttribute('data-photo-url') || '').trim();
      tryAddPresenter(em, photo ? { photoURL: photo } : undefined);
      return;
    }
    if (action === 'remove-presenter') {
      const email = normalizeText(button.getAttribute('data-email') || '');
      if (!presId() || !email) return;
      const next = currentPresenterEmails().filter((value) => normalizeText(value) !== email);
      state.busyPresenterEmail = email;
      state.banner = 'Removing presenter…';
      render();
      presentationService
        .updatePresentationPrefs(presId(), { presenters: next })
        .then(() => presentationService.getPresentationDoc(presId()))
        .then((doc) => {
          state.presentation = doc;
          state.banner = 'Presenter removed.';
        })
        .catch(() => {
          state.banner = 'Could not remove presenter.';
        })
        .finally(() => {
          state.busyPresenterEmail = '';
          render();
        });
      return;
    }
    if (action === 'approve-request' || action === 'deny-request') {
      const requestId = String(button.getAttribute('data-request-id') || '').trim();
      if (!presId() || !requestId) return;
      const approve = action === 'approve-request';
      const approvedReq = approve ? state.requests.find((r) => r.id === requestId) : null;
      state.busyRequestId = requestId;
      state.requestBanner = approve ? 'Granting presenter rights…' : 'Denying presenter request…';
      render();
      presentationService
        .reviewPresenterRequest(presId(), requestId, approve)
        .then(async () => {
          state.requests = await presentationService.getPresenterRequests(presId()).catch(() => []);
          state.presentation = await presentationService.getPresentationDoc(presId()).catch(() => null);
          state.requestBanner = approve ? 'Presenter request approved.' : 'Presenter request denied.';
          if (approve && approvedReq) {
            mergeUserProfilePhoto(normalizeText(approvedReq.email), approvedReq.photoURL);
          }
        })
        .catch(() => {
          state.requestBanner = approve ? 'Could not approve presenter request.' : 'Could not deny presenter request.';
        })
        .finally(() => {
          state.busyRequestId = '';
          render();
        });
      return;
    }
  }

  function tryAddPresenter(rawEmail: string, profileHint?: { photoURL?: string }): void {
    const email = normalizeText(rawEmail);
    if (!presId()) return;
    if (!/.+@.+\..+/.test(email)) {
      state.banner = 'Enter a valid presenter email address.';
      render();
      return;
    }
    if (existingPresenterSet().has(email)) {
      state.banner = 'That presenter is already assigned.';
      render();
      return;
    }
    if (adminEmailSet().has(email)) {
      state.banner = 'That user is already an admin and can manage every presentation.';
      render();
      return;
    }
    const hasHint = !!(profileHint?.photoURL && isSafePhotoUrl(profileHint.photoURL));
    state.busyPresenterEmail = email;
    state.banner = 'Adding presenter…';
    render();
    presentationService
      .updatePresentationPrefs(presId(), { presenters: currentPresenterEmails().concat(email) })
      .then(() =>
        Promise.all([
          presentationService.getPresentationDoc(presId()),
          presentationService.getPresenterRequests(presId()).catch(() => []),
          hasHint
            ? Promise.resolve({} as Record<string, { photoURL?: string }>)
            : presentationService
                .lookupUserProfilesByEmails([email])
                .catch(() => ({} as Record<string, { photoURL?: string; displayName?: string }>))
        ])
      )
      .then(([doc, requests, profMap]) => {
        state.presentation = doc;
        state.requests = (requests as PresentationRequestState[]) || [];
        presenterSearchDraft = '';
        state.searchToken++;
        state.banner = 'Presenter added successfully.';
        paintSuggestionList([]);
        if (hasHint && profileHint?.photoURL) {
          mergeUserProfilePhoto(email, profileHint.photoURL);
        } else {
          const row = profMap[email];
          if (row?.photoURL) mergeUserProfilePhoto(email, row.photoURL);
        }
      })
      .catch(() => {
        state.banner = 'Could not add presenter.';
      })
      .finally(() => {
        state.busyPresenterEmail = '';
        render();
      });
  }

  function handleInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    if (target.matches('[data-field="presenter-search"]')) {
      presenterSearchDraft = target.value;
      scheduleSearch();
    }
  }

  function handleFocusOut(event: FocusEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target || !target.matches('[data-field="presenter-search"]')) return;
    window.setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      if (active && active.matches('[data-field="presenter-search"]') && container.contains(active)) {
        // Ignore synthetic blur events caused by panel re-renders; keep typing stable.
        return;
      }
      paintSuggestionList([]);
    }, 120);
  }

  container.addEventListener('click', handleClick);
  container.addEventListener('input', handleInput);
  container.addEventListener('focusout', handleFocusOut);

  void authService.subscribeUserChange?.(() => {
    void refreshPresentationData({ refreshRole: true });
  });
  void roleService?.subscribe?.(() => {
    const nextRole = String(roleService?.getRole?.() || 'viewer');
    const downgraded = isManagerRole(lastKnownRole) && !isManagerRole(nextRole);
    lastKnownRole = nextRole;
    if (
      downgraded &&
      !demotionReloadScheduled &&
      typeof window !== 'undefined' &&
      // Keep tests deterministic; production/runtime still reloads.
      !(window as unknown as { __LECTURA_DB_TEST__?: unknown }).__LECTURA_DB_TEST__
    ) {
      demotionReloadScheduled = true;
      window.setTimeout(() => {
        try {
          window.location.reload();
        } catch {
          // If reload is blocked/unavailable, fallback to normal live refresh.
          void refreshPresentationData({ refreshRole: true });
        }
      }, 40);
      return;
    }
    void refreshPresentationData();
  });

  authService.ensureAuth?.(() => {});
  void refreshPresentationData({ refreshRole: true });

  // The panel lives for the lifetime of the settings page; listeners are
  // intentionally retained until the page is discarded.
}
